import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaFileContract, FaCheckCircle, FaArrowLeft } from "react-icons/fa";
import { useOrganization } from "../../context/OrganizationContext";
import { useAuth } from "../../context/AuthContext";
import { useDpaAcceptance } from "../../hooks/useDpaAcceptance";
import { PrimaryButton, SecondaryButton, LoadingSpinner, Card } from "../../components/UI/DesignStandards";
import InlineAlert from "../../components/UI/DesignStandards/Notifications/InlineAlert";
import { notify } from "../../components/UI/DesignStandards/Notifications/notify";
import { getCurrentDpaVersion } from "../../config/dpaConfig";
import SEOHead from "../../components/SEO/SEOHead";

const DPAAcceptance = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const { acceptDpa, error: dpaError, hasAccepted } = useDpaAcceptance(
    currentOrganization?.id
  );

  const currentVersion = getCurrentDpaVersion();

  // Redirect if already accepted
  React.useEffect(() => {
    if (hasAccepted && !accepting) {
      navigate("/organization");
    }
  }, [hasAccepted, navigate, accepting]);

  // Redirect if no organization
  if (!currentOrganization) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <div className="p-6 text-center">
            <p className="text-brand-muted mb-4">
              Please select an organization to accept the Data Processing Agreement.
            </p>
            <Link
              to="/organization"
              className="text-brand-gold-ink hover:text-brand-charcoal underline"
            >
              Go to Organization Dashboard
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const handleAccept = async () => {
    if (!accepted) return;

    setAccepting(true);
    try {
      const result = await acceptDpa();
      if (result?.success) {
        // Show success notification
        notify.success("Data Processing Agreement accepted successfully");
        // Redirect to dashboard after successful acceptance
        navigate("/organization");
      }
    } catch (error) {
      console.error("Error accepting DPA:", error);
      setAccepting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Accept Data Processing Agreement - Bowling Tournament App"
        description="Accept the Data Processing Agreement for your organization to manage tournaments and process bowler data."
        canonicalUrl="/organization/dpa-acceptance"
        noIndex={true}
        noFollow={true}
      />
      <div className="min-h-screen bg-brand-cream py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/organization"
          className="inline-flex items-center text-sm text-brand-muted hover:text-brand-charcoal mb-6"
          aria-label="Return to organization dashboard"
        >
          <FaArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Organization Dashboard
        </Link>

        {/* Header */}
        <div className="card-board overflow-hidden mb-6">
          <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
            <div className="flex items-center">
              <div className="flex-shrink-0" aria-hidden="true">
                <div className="h-12 w-12 rounded-lg bg-brand-line flex items-center justify-center">
                  <FaFileContract className="h-6 w-6 text-brand-cream" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="font-display font-bold text-xl md:text-2xl text-brand-cream tracking-tight">
                  Data Processing Agreement
                </h1>
                <p className="text-sm text-brand-mist mt-1">
                  Version {currentVersion} • {currentOrganization.name}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <Card className="mb-6">
          <div className="p-6">
            <div className="prose prose-gray max-w-none">
              <div className="card-inset border-l-4 border-brand-charcoal p-4 mb-6">
                <p className="text-brand-charcoal text-sm">
                  <strong>Important:</strong> As an organization owner or admin, you can
                  accept the Data Processing Agreement (DPA) on behalf of your organization.
                  This agreement outlines your responsibilities as a Data Controller.
                </p>
              </div>

              <p className="mb-4">
                Please review the{" "}
                <Link
                  to="/data-processing-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-gold-ink hover:text-brand-charcoal underline"
                >
                  Data Processing Agreement
                </Link>{" "}
                and{" "}
                <Link
                  to="/data-retention-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-gold-ink hover:text-brand-charcoal underline"
                >
                  Data Retention Policy
                </Link>{" "}
                before accepting.
              </p>

              <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                <p className="text-brand-charcoal text-sm">
                  <strong>Key Points:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2 text-sm text-brand-muted">
                  <li>
                    You act as the Data Controller for bowler data you collect
                  </li>
                  <li>
                    You are responsible for having authorization to enter bowler
                    personal information
                  </li>
                  <li>
                    You must use data solely for tournament administration
                  </li>
                  <li>
                    You must notify the platform of any data breaches
                  </li>
                  <li>
                    The platform acts only as a Data Processor
                  </li>
                </ul>
              </div>

              {/* Acceptance Checkbox */}
              <div className="mt-6">
                <fieldset>
                  <legend className="sr-only">
                    Data Processing Agreement Acceptance
                  </legend>
                  <label className="flex items-start cursor-pointer">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        className="focus:ring-brand-gold-ink h-4 w-4 text-brand-charcoal border-brand-charcoal/20 rounded"
                        disabled={accepting}
                        aria-describedby={dpaError ? "dpa-error-message" : "dpa-checkbox-description"}
                        aria-required="true"
                        aria-invalid={dpaError ? "true" : "false"}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <span id="dpa-checkbox-description" className="text-brand-muted">
                        I confirm I am authorized to act on behalf of{" "}
                        <strong>{currentOrganization.name}</strong> and agree to
                        the{" "}
                        <Link
                          to="/data-processing-agreement"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                          aria-label="Data Processing Agreement (opens in new window)"
                        >
                          Data Processing Agreement
                        </Link>{" "}
                        and{" "}
                        <Link
                          to="/data-retention-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                          aria-label="Data Retention Policy (opens in new window)"
                        >
                          Data Retention Policy
                        </Link>
                        .
                      </span>
                    </div>
                  </label>
                </fieldset>
              </div>

              {dpaError && (
                <div id="dpa-error-message" className="mt-4" role="alert" aria-live="polite">
                  <InlineAlert
                    type="error"
                    title="Error"
                    message={dpaError}
                  />
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <SecondaryButton
            onClick={() => navigate("/organization")}
            type="button"
            className="!px-3 !py-2 text-sm"
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleAccept}
            disabled={!accepted || accepting}
            className="min-w-[160px]"
            aria-label={accepting ? "Accepting Data Processing Agreement" : "Accept Data Processing Agreement and continue"}
            aria-busy={accepting}
          >
            {accepting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" aria-hidden="true" />
                <span aria-live="polite">Accepting...</span>
              </>
            ) : (
              <>
                <FaCheckCircle className="mr-2" aria-hidden="true" />
                Accept & Continue
              </>
            )}
          </PrimaryButton>
        </div>
        </div>
      </div>
    </>
  );
};

export default DPAAcceptance;

